<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { USER_MARK_SHARE } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import AdpDocumentTooltipUserListNext from './adp-document-tooltip-user-list-next.vue';
import AdpDocumentTooltipUserList from './adp-document-tooltip-user-list.vue';

const emit = defineEmits<{
  handlePopupMenuClick: [];
  handleMarkClick: [];
}>();

const props = withDefaults(
  defineProps<{
    isMarked: boolean;
    markedUsers: {
      id: number;
      name: string;
    }[];
    disabled?: boolean;
    isMarking?: boolean;
    small?: boolean;
    showAction?: boolean;
  }>(),
  {
    disabled: false,
    isMarking: false,
    small: false,
    showAction: true,
  },
);

const isNextLayout = useIsNextLayout();

const isInteractingMarkButton = ref(false);
// クリックで開いた場合は mouseleave では閉じず、外側クリックで閉じる。
const isPinned = ref(false);

const isShowPopupMenu = computed(
  () => isInteractingMarkButton.value && !props.isMarked,
);

const markCount = computed(() => props.markedUsers.length);

const tooltipUsers = computed(() =>
  props.markedUsers.map(user => ({
    id: user.id,
    name: user.name,
    to: { name: 'userMarks', params: { userId: user.id } },
  })),
);

const showPopupMenu = () => {
  if (props.disabled) return;
  isInteractingMarkButton.value = true;
};

const hidePopupMenu = () => {
  if (isPinned.value) return;
  isInteractingMarkButton.value = false;
};

const openPopupMenu = () => {
  if (props.disabled) return;
  isPinned.value = true;
  isInteractingMarkButton.value = true;
};

const closePopupMenu = () => {
  isPinned.value = false;
  isInteractingMarkButton.value = false;
};

const handlePopupMenuButtonClick = () => {
  if (props.disabled || props.isMarking) return;
  closePopupMenu();
  emit('handlePopupMenuClick');
};

const handleMarkButtonClick = () => {
  if (props.disabled || props.isMarking) return;
  closePopupMenu();
  emit('handleMarkClick');
};

onMounted(() => {
  document.addEventListener('click', closePopupMenu);
});

onUnmounted(() => {
  document.removeEventListener('click', closePopupMenu);
});
</script>

<template>
  <div
    class="mark-button-field"
    :class="{ 'mark-button-active': isMarked && !disabled, disabled, small }"
    @mouseleave="hidePopupMenu"
  >
    <VTooltip theme="menu" placement="top" @click="handleMarkButtonClick">
      <div class="mark-button-area">
        <button class="mark-button" :disabled="disabled">
          <DgrIcon
            class="mark-icon"
            :class="{ disabled }"
            size="small"
            :name="isMarked ? 'check-circle-fill' : 'check-circle'"
            :keep-fill="false"
            alt="mark icon"
          />
          <div v-if="markCount > 0" class="mark-total-count c-title-bold--s">
            {{ markCount }}
          </div>
        </button>
      </div>

      <template #popper>
        <AdpDocumentTooltipUserListNext
          v-if="isNextLayout"
          action="mark"
          :users="tooltipUsers"
        />
        <AdpDocumentTooltipUserList
          v-else
          :users="tooltipUsers"
          :empty-text="USER_MARK_SHARE"
        />
      </template>
    </VTooltip>
    <button
      v-if="!isMarked && !disabled && showAction"
      class="arrow-button-area"
      @mouseover="showPopupMenu"
      @click.stop.prevent="openPopupMenu"
    >
      <DgrIcon
        size="small"
        class="arrow-icon"
        :name="isInteractingMarkButton ? 'angle-up' : 'angle-down'"
        alt="arrow icon"
      />
    </button>
    <div v-if="isShowPopupMenu" class="action-menu-container">
      <button
        class="popup-menu-item c-text-regular--m"
        @click.stop.prevent="handlePopupMenuButtonClick"
      >
        マイタグ・マイメモをしてマーク
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.mark-button-field {
  position: relative;
  width: fit-content;
  display: flex;
  min-width: 60px;
  border-radius: 4px;
  align-items: center;
  cursor: pointer;
  &:not(.disabled):hover {
    background-color: $color-gray200;
  }

  &.disabled {
    cursor: not-allowed;

    .mark-icon.disabled,
    .mark-total-count {
      color: $color-gray600;
    }
  }

  &.small {
    min-width: 58px;

    .mark-button-area {
      padding: 4px 0 4px 4px;
    }

    .arrow-button-area {
      padding: 4px 4px 4px 2px;
    }

    &.mark-button-active .mark-button-area {
      padding: 4px;
    }
  }

  .action-menu-container {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: var(--z-action-menu);
    width: 240px;
    padding: 8px 0;
    background: #fff;
    border-radius: 4px;
    border: 1px solid $color-gray400;
    cursor: default;
  }
}

.mark-button-area {
  display: flex;
  align-items: center;
  padding: 8px 0px 8px 10px;
}

.mark-button {
  all: unset;
  display: flex;
  align-items: center;

  &:disabled {
    border: none;
  }
}

.mark-icon {
  margin-right: 4px;
}

.arrow-button-area {
  all: unset;
  display: flex;
  align-items: center;
  padding: 8px 10px 8px 0;
}

.popup-menu-item {
  border-radius: unset;
  background-color: unset;
  width: 100%;
  cursor: pointer;
  padding: 4px 12px;
  height: auto;
  border: none;

  &:hover {
    background: $color-gray200;
  }
}

.arrow-icon {
  padding-left: 4px;
}

.mark-button-active {
  & .mark-icon {
    color: $color-green600;
  }

  & .mark-total-count {
    color: $color-green600;
  }

  & .mark-button-area {
    width: 100%;
  }
}
</style>
